import { forwardRef } from "react";

const LOGO_URL = "/protocall-logo.png";

// Base64 fallback for PDF generation (Orange P logo placeholder)
const LOGO_FALLBACK = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHJ4PSIxMiIgZmlsbD0iI2Y5NzMxNiIvPjx0ZXh0IHg9IjMwIiB5PSI0MiIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjMyIiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlA8L3RleHQ+PC9zdmc+";

const BUSINESS_INFO = {
  name: "ProToCall",
  tagline: "Professional Handyman Services",
  address: "Saint Lucia, Caribbean",
  phone: "+1 758 723 0202",
  email: "info@cariphs.com",
};

export const InvoiceTemplate = forwardRef(({ invoice, className = "" }, ref) => {
  const isQuote = invoice?.document_type === "quote";
  const documentTitle = isQuote ? "QUOTATION" : "INVOICE";
  
  const formatCurrency = (amount) => {
    return `XCD $${(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  if (!invoice) return null;

  return (
    <div
      ref={ref}
      className={`bg-white ${className}`}
      style={{
        width: "210mm",
        minHeight: "297mm",
        padding: "20mm",
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
        color: "#1e293b",
        boxSizing: "border-box",
      }}
      data-testid="invoice-template"
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "30px", borderBottom: "3px solid #fb3700", paddingBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <img 
            src={LOGO_URL} 
            alt="ProToCall Logo" 
            style={{ height: "60px", width: "auto" }}
            crossOrigin="anonymous"
            onError={(e) => { e.target.src = LOGO_FALLBACK; }}
          />
          <div>
            <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "bold", color: "#0f172a" }}>
              {BUSINESS_INFO.name}
            </h1>
            <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "#64748b" }}>
              {BUSINESS_INFO.tagline}
            </p>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: "28px", 
            fontWeight: "bold", 
            color: isQuote ? "#3b82f6" : "#fb3700",
            letterSpacing: "2px"
          }}>
            {documentTitle}
          </h2>
          <p style={{ margin: "5px 0 0 0", fontSize: "14px", fontWeight: "600", color: "#0f172a" }}>
            #{invoice.invoice_number}
          </p>
        </div>
      </div>

      {/* Business & Customer Info */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "30px" }}>
        {/* From */}
        <div style={{ width: "45%" }}>
          <h3 style={{ margin: "0 0 10px 0", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase", letterSpacing: "1px" }}>
            From
          </h3>
          <div style={{ backgroundColor: "#f8fafc", padding: "15px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
            <p style={{ margin: "0 0 5px 0", fontWeight: "600", fontSize: "14px" }}>{BUSINESS_INFO.name}</p>
            <p style={{ margin: "0 0 3px 0", color: "#475569" }}>{BUSINESS_INFO.address}</p>
            <p style={{ margin: "0 0 3px 0", color: "#475569" }}>{BUSINESS_INFO.phone}</p>
            <p style={{ margin: 0, color: "#475569" }}>{BUSINESS_INFO.email}</p>
          </div>
        </div>

        {/* To */}
        <div style={{ width: "45%" }}>
          <h3 style={{ margin: "0 0 10px 0", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase", letterSpacing: "1px" }}>
            Bill To
          </h3>
          <div style={{ backgroundColor: "#f8fafc", padding: "15px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
            <p style={{ margin: "0 0 5px 0", fontWeight: "600", fontSize: "14px" }}>{invoice.user_name || "Customer"}</p>
            {invoice.user_address && <p style={{ margin: "0 0 3px 0", color: "#475569" }}>{invoice.user_address}</p>}
            {invoice.user_phone && <p style={{ margin: "0 0 3px 0", color: "#475569" }}>{invoice.user_phone}</p>}
            {invoice.user_email && <p style={{ margin: 0, color: "#475569" }}>{invoice.user_email}</p>}
          </div>
        </div>
      </div>

      {/* Document Details */}
      <div style={{ display: "flex", gap: "20px", marginBottom: "30px" }}>
        <div style={{ flex: 1, backgroundColor: "#fff2ee", padding: "12px 15px", borderRadius: "8px", borderLeft: "4px solid #fb3700" }}>
          <p style={{ margin: 0, fontSize: "10px", color: "#9a3412", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            {isQuote ? "Quote" : "Invoice"} Date
          </p>
          <p style={{ margin: "3px 0 0 0", fontWeight: "600", fontSize: "13px" }}>{formatDate(invoice.created_at)}</p>
        </div>
        <div style={{ flex: 1, backgroundColor: "#fef2f2", padding: "12px 15px", borderRadius: "8px", borderLeft: "4px solid #ef4444" }}>
          <p style={{ margin: 0, fontSize: "10px", color: "#b91c1c", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            {isQuote ? "Valid Until" : "Due Date"}
          </p>
          <p style={{ margin: "3px 0 0 0", fontWeight: "600", fontSize: "13px" }}>{formatDate(invoice.due_date)}</p>
        </div>
        <div style={{ 
          flex: 1, 
          backgroundColor: invoice.status === "paid" ? "#f0fdf4" : "#fefce8", 
          padding: "12px 15px", 
          borderRadius: "8px", 
          borderLeft: `4px solid ${invoice.status === "paid" ? "#22c55e" : "#eab308"}` 
        }}>
          <p style={{ margin: 0, fontSize: "10px", color: invoice.status === "paid" ? "#166534" : "#a16207", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Status
          </p>
          <p style={{ margin: "3px 0 0 0", fontWeight: "600", fontSize: "13px", textTransform: "capitalize" }}>
            {invoice.status}
          </p>
        </div>
      </div>

      {/* Description */}
      {invoice.description && (
        <div style={{ marginBottom: "25px" }}>
          <h3 style={{ margin: "0 0 10px 0", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase", letterSpacing: "1px" }}>
            Description
          </h3>
          <p style={{ margin: 0, padding: "15px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0", lineHeight: "1.6" }}>
            {invoice.description}
          </p>
        </div>
      )}

      {/* Line Items Table */}
      <div style={{ marginBottom: "25px" }}>
        <h3 style={{ margin: "0 0 10px 0", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase", letterSpacing: "1px" }}>
          Service Details
        </h3>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ backgroundColor: "#0f172a" }}>
              <th style={{ padding: "12px 15px", textAlign: "left", color: "white", fontSize: "11px", fontWeight: "600", borderRadius: "8px 0 0 0" }}>
                Description
              </th>
              <th style={{ padding: "12px 15px", textAlign: "center", color: "white", fontSize: "11px", fontWeight: "600", width: "80px" }}>
                Qty
              </th>
              <th style={{ padding: "12px 15px", textAlign: "right", color: "white", fontSize: "11px", fontWeight: "600", width: "120px" }}>
                Unit Price
              </th>
              <th style={{ padding: "12px 15px", textAlign: "right", color: "white", fontSize: "11px", fontWeight: "600", width: "120px", borderRadius: "0 8px 0 0" }}>
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {invoice.line_items && invoice.line_items.length > 0 ? (
              invoice.line_items.map((item, index) => (
                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
                  <td style={{ padding: "12px 15px", borderBottom: "1px solid #e2e8f0" }}>
                    {item.description}
                  </td>
                  <td style={{ padding: "12px 15px", textAlign: "center", borderBottom: "1px solid #e2e8f0" }}>
                    {item.quantity}
                  </td>
                  <td style={{ padding: "12px 15px", textAlign: "right", borderBottom: "1px solid #e2e8f0" }}>
                    {formatCurrency(item.unit_price)}
                  </td>
                  <td style={{ padding: "12px 15px", textAlign: "right", borderBottom: "1px solid #e2e8f0", fontWeight: "500" }}>
                    {formatCurrency(item.amount)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td style={{ padding: "12px 15px", borderBottom: "1px solid #e2e8f0" }}>
                  {invoice.description || "Service"}
                </td>
                <td style={{ padding: "12px 15px", textAlign: "center", borderBottom: "1px solid #e2e8f0" }}>
                  1
                </td>
                <td style={{ padding: "12px 15px", textAlign: "right", borderBottom: "1px solid #e2e8f0" }}>
                  {formatCurrency(invoice.subtotal || invoice.amount)}
                </td>
                <td style={{ padding: "12px 15px", textAlign: "right", borderBottom: "1px solid #e2e8f0", fontWeight: "500" }}>
                  {formatCurrency(invoice.subtotal || invoice.amount)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "30px" }}>
        <div style={{ width: "280px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #e2e8f0" }}>
            <span style={{ color: "#64748b" }}>Subtotal</span>
            <span style={{ fontWeight: "500" }}>{formatCurrency(invoice.subtotal || invoice.amount)}</span>
          </div>
          {invoice.discount_amount > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #e2e8f0" }}>
              <span style={{ color: "#16a34a" }}>
                Discount{invoice.discount_type === "percent" ? ` (${invoice.discount_value}%)` : ""}
              </span>
              <span style={{ fontWeight: "500", color: "#16a34a" }}>−{formatCurrency(invoice.discount_amount)}</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #e2e8f0" }}>
            <span style={{ color: "#64748b" }}>Tax ({(invoice.tax_rate || 0) * 100}%)</span>
            <span style={{ fontWeight: "500" }}>{formatCurrency(invoice.tax_amount || 0)}</span>
          </div>
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            padding: "15px", 
            marginTop: "10px",
            backgroundColor: "#0f172a", 
            borderRadius: "8px",
            color: "white"
          }}>
            <span style={{ fontSize: "14px", fontWeight: "600" }}>TOTAL</span>
            <span style={{ fontSize: "18px", fontWeight: "bold" }}>{formatCurrency(invoice.amount)}</span>
          </div>
          {invoice.amount_paid > 0 && invoice.status !== "paid" && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #e2e8f0" }}>
                <span style={{ color: "#22c55e" }}>Amount Paid</span>
                <span style={{ fontWeight: "500", color: "#22c55e" }}>-{formatCurrency(invoice.amount_paid)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0" }}>
                <span style={{ fontWeight: "600", color: "#ef4444" }}>Balance Due</span>
                <span style={{ fontWeight: "bold", color: "#ef4444" }}>{formatCurrency(invoice.amount - invoice.amount_paid)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Notes */}
      {invoice.notes && (
        <div style={{ marginBottom: "30px" }}>
          <h3 style={{ margin: "0 0 10px 0", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase", letterSpacing: "1px" }}>
            Notes
          </h3>
          <p style={{ margin: 0, padding: "15px", backgroundColor: "#fffbeb", borderRadius: "8px", border: "1px solid #fde68a", lineHeight: "1.6", fontSize: "11px" }}>
            {invoice.notes}
          </p>
        </div>
      )}

      {/* Footer */}
      <div style={{ 
        marginTop: "auto", 
        paddingTop: "20px", 
        borderTop: "2px solid #e2e8f0",
        textAlign: "center"
      }}>
        <p style={{ margin: "0 0 5px 0", fontSize: "11px", color: "#64748b" }}>
          Thank you for choosing ProToCall for your property maintenance needs!
        </p>
        <p style={{ margin: 0, fontSize: "10px", color: "#94a3b8" }}>
          For questions about this {isQuote ? "quotation" : "invoice"}, please contact us at {BUSINESS_INFO.phone} or {BUSINESS_INFO.email}
        </p>
      </div>
    </div>
  );
});

InvoiceTemplate.displayName = "InvoiceTemplate";

export default InvoiceTemplate;
